import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, HostListener } from '@angular/core'
import { PopoverService } from './popover.service'
import { Subscription } from 'rxjs'

@Component({ selector: 'app-popover', templateUrl: './popover.component.html', styleUrls: ['./popover.component.scss'], standalone: false })
export class PopoverComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('el') el: ElementRef

  /** Popover left by default is left minus 111 pixels */
  @Input() left: number

  /** Popover top by default is top minus 20 pixels */
  @Input() top: number

  visible: boolean = false
  _trapEvent: any
  private _resizeTimeout: any
  private _popoverId: string
  private _closeAllSubscription: Subscription

  constructor(private popoverService: PopoverService) {
    this._trapEvent = this.onTrap.bind(this)
    this._popoverId = 'popover-' + Math.random().toString(36).substr(2, 9)
  }

  @HostListener('window:resize', ['$event'])
  onResize(_event: any) {
    if (this.visible) {
      // Debounce resize events to avoid excessive repositioning
      clearTimeout(this._resizeTimeout)
      this._resizeTimeout = setTimeout(() => {
        this.createFixator()
      }, 100)
    }
  }

  ngOnInit(): void {
    this._closeAllSubscription = this.popoverService.closeAll$.subscribe((exceptId) => {
      if (exceptId !== this._popoverId && this.visible) {
        this.visible = false
        this.checkState()
        this.el.nativeElement.setAttribute('hidden', 'true')
      }
    })
  }

  onTrap(e: Event) {
    const el = e.target as HTMLElement

    // Check if clicked element is inside a PrimeNG overlay that's appended to body (NOT parent dialogs)
    // We only care about overlays created by components inside our popover (dropdowns, treeselects, etc.)
    const overlayElement = el?.closest('.p-multiselect-panel, .p-dropdown-panel, .p-treeselect-panel, .p-overlaypanel, .p-sidebar')
    // Only ignore the click if the overlay is NOT an ancestor of our popover (i.e., it's appended to body)
    const isPrimeNGOverlay = overlayElement && !overlayElement.contains(this.el.nativeElement)

    // Check if the clicked element is the trigger button (sibling of popover)
    const parentElement = this.el.nativeElement.parentElement
    const triggerButton = parentElement?.querySelector('button')
    const isButtonOrChild = triggerButton && (el === triggerButton || triggerButton.contains(el))

    if (this.visible && !this.el.nativeElement.contains(el) && el != this.el.nativeElement && !isPrimeNGOverlay && !isButtonOrChild) {
      this.toggle()
    }
  }

  ngAfterViewInit() {
    this.checkState()
  }

  ngOnDestroy() {
    this.unsubscribeEvents()
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout)
    }
    if (this._closeAllSubscription) {
      this._closeAllSubscription.unsubscribe()
    }
  }
  createFixator() {
    if (!this.left) {
      this.left = 111
    }

    if (!this.top) {
      this.top = 20
    }

    const triggerElement = this.el.nativeElement.parentNode
    const popoverElement = this.el.nativeElement

    if (!triggerElement) return

    const triggerRect = triggerElement.getBoundingClientRect()
    const popoverRect = popoverElement.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    // Calculate initial position (below trigger)
    let top = triggerRect.bottom + 5 // 5px gap
    let left = triggerRect.left - this.left

    // Check if popover fits below the trigger
    const spaceBelow = viewportHeight - triggerRect.bottom
    const spaceAbove = triggerRect.top
    const popoverHeight = popoverRect.height || 200 // fallback height

    // Smart positioning logic
    if (spaceBelow < popoverHeight && spaceAbove > popoverHeight) {
      // Position above trigger if more space above
      top = triggerRect.top - popoverHeight - 5
      popoverElement.classList.add('popover-above')
      popoverElement.classList.remove('popover-below', 'popover-side')
    } else if (spaceBelow < popoverHeight && spaceAbove < popoverHeight) {
      // If no space above or below, position to the side with scroll
      const spaceRight = viewportWidth - triggerRect.right
      const spaceLeft = triggerRect.left

      if (spaceRight > spaceLeft) {
        // Position to the right
        left = triggerRect.right + 5
        top = Math.min(triggerRect.top, viewportHeight - popoverHeight - 10)
      } else {
        // Position to the left
        left = triggerRect.left - (popoverRect.width || 300) - 5
        top = Math.min(triggerRect.top, viewportHeight - popoverHeight - 10)
      }

      // Ensure we don't go above viewport
      top = Math.max(10, top)

      popoverElement.classList.add('popover-side')
      popoverElement.classList.remove('popover-above', 'popover-below')
    } else {
      // Default position below trigger
      popoverElement.classList.add('popover-below')
      popoverElement.classList.remove('popover-above', 'popover-side')
    }

    // Ensure popover doesn't go outside viewport horizontally
    const popoverWidth = popoverRect.width || 300
    if (left + popoverWidth > viewportWidth) {
      left = viewportWidth - popoverWidth - 10
    }
    if (left < 10) {
      left = 10
    }

    // Apply the calculated position
    popoverElement.style.top = top + 'px'
    popoverElement.style.left = left + 'px'
    popoverElement.style.position = 'fixed'
    popoverElement.style.zIndex = '10000'
  }
  destroyFixator() {/* intentionally empty */}
  subscribeEvents() {
    this.createFixator()
    requestAnimationFrame(() => {
      if (this.visible) {
        document.addEventListener('mousedown', this._trapEvent, true)
      }
    })
  }
  unsubscribeEvents() {
    document?.removeEventListener('mousedown', this._trapEvent, true)
  }
  checkState() {
    if (this.visible) {
      this.subscribeEvents()
    } else {
      this.unsubscribeEvents()
    }
  }
  toggle(e?: Event) {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    this.visible = !this.visible

    if (this.visible) {
      this.popoverService.closeAllExcept(this._popoverId)
      this.el.nativeElement.removeAttribute('hidden')
      // Reading offsetHeight forces a synchronous reflow so the show transition restarts
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.el.nativeElement.offsetHeight
      this.checkState()
    } else {
      this.checkState()
      this.el.nativeElement.setAttribute('hidden', 'true')
    }
  }
}
